/**
 * Phase 67-07b — derives reboot-looping kiosks from audit_log.
 *
 * Backend RebootLoopDetector emits `reboot_loop_detected` audit rows
 * (source=`reboot_loop_detector`) with target_path=`machine:<cpu_id>`.
 * This hook fetches the last 24h of those rows, extracts unique cpu_ids,
 * and exposes the resulting set for badge + per-row icon rendering.
 *
 * ALERT-ONLY: never triggers a reboot or any state mutation.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { listAudit } from "@/api/audit";

/** Look back window matches backend dedupe — 24h. */
export const REBOOT_LOOP_LOOKBACK_MS = 24 * 60 * 60 * 1000;

export function useRebootLoops() {
  const dateFrom = useMemo(
    () => new Date(Date.now() - REBOOT_LOOP_LOOKBACK_MS).toISOString(),
    [],
  );

  const q = useQuery({
    queryKey: ["audit", "all", { source: "reboot_loop_detector", date_from: dateFrom }],
    queryFn: () =>
      listAudit({ source: "reboot_loop_detector", date_from: dateFrom }, null, 100),
    // No client-side polling — backed by audit table writes from the
    // RebootLoopDetector poll loop (60s cadence). Phase 67-05 WS push
    // invalidates this query when a new audit row lands.
    staleTime: 60 * 1000,
  });

  const affectedCpuIds = useMemo(() => {
    if (!q.data?.items) return new Set<string>();
    const ids = new Set<string>();
    for (const row of q.data.items) {
      // target_path is `machine:<cpu_id>` — strip the prefix.
      if (row.target_path?.startsWith("machine:")) {
        ids.add(row.target_path.slice("machine:".length));
      }
    }
    return ids;
  }, [q.data]);

  return {
    ...q,
    affectedCpuIds,
    count: affectedCpuIds.size,
  };
}
